import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useState,
} from "react";
import { AppState } from "react-native";

import { updatePushDeviceLocale } from "@/lib/pushNotifications/registerPushDevice";
import { useDispatch } from "react-redux";
import { s4pApi } from "../query/apiSlice";
import i18n, {
    type AppLanguage,
    detectDeviceLanguage,
    SYSTEM_LANGUAGE,
} from "./i18n";
import { getStoredLanguage, setStoredLanguage } from "./languageStorage";

const LanguagePreferenceContext = createContext<{
    preference: AppLanguage | null;
    changeLanguage: (language: AppLanguage) => Promise<void>;
    systemLanguage: Exclude<AppLanguage, "system" | "browser">;
} | null>(null);

export function I18nProvider({ children }: { children: React.ReactNode }) {
    const [preference, setPreference] = useState<AppLanguage | null>(null);
    const [systemLanguage, setSystemLanguage] = useState<"cs" | "en">(
        detectDeviceLanguage,
    );
    const dispatch = useDispatch();

    useEffect(() => {
        let cancelled = false;

        void (async () => {
            const stored = await getStoredLanguage();
            const nextPreference = stored || SYSTEM_LANGUAGE;
            if (cancelled) {
                return;
            }

            setPreference(nextPreference);

            const languageToUse =
                nextPreference === SYSTEM_LANGUAGE
                    ? detectDeviceLanguage()
                    : nextPreference;

            setSystemLanguage(detectDeviceLanguage());

            if (languageToUse !== i18n.language) {
                await i18n.changeLanguage(languageToUse);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        const subscription = AppState.addEventListener(
            "change",
            (nextState) => {
                if (nextState !== "active") {
                    return;
                }

                const detected = detectDeviceLanguage();
                setSystemLanguage(detected);

                if (
                    preference === SYSTEM_LANGUAGE &&
                    detected !== i18n.language
                ) {
                    void i18n.changeLanguage(detected);
                    dispatch(s4pApi.util.invalidateTags(["ParentMessages"]));
                }
            },
        );

        return () => subscription.remove();
    }, [dispatch, preference]);

    const changeLanguage = useCallback(
        async (language: AppLanguage) => {
            const languageToUse =
                language === SYSTEM_LANGUAGE
                    ? detectDeviceLanguage()
                    : language;
            await i18n.changeLanguage(languageToUse);
            await setStoredLanguage(language);
            setPreference(language);
            setSystemLanguage(detectDeviceLanguage());
            // Profile fields and inbox copy come from the API in the requested language.
            dispatch(
                s4pApi.util.invalidateTags([
                    "Profiles",
                    "Profile",
                    "ParentMessages",
                ]),
            );
            const locale = languageToUse === "cs" ? "cs" : "en";
            void updatePushDeviceLocale(locale);
        },
        [dispatch],
    );

    return (
        <LanguagePreferenceContext.Provider
            value={{
                preference,
                changeLanguage,
                systemLanguage,
            }}
        >
            {children}
        </LanguagePreferenceContext.Provider>
    );
}

export function useLanguagePreference() {
    const ctx = useContext(LanguagePreferenceContext);
    if (!ctx) throw new Error("useLanguagePreference outside I18nProvider");
    return ctx;
}
